import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useIntl } from "react-intl";
import {
  Skus,
  ListResponseOfSkuListItem,
  ResponseOfSkus,
  SkuFilter,
  GetSkusRequest,
} from "services/KnowfullyAdminClient";
import useGlobalContext from "hooks/globalData";
import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import contextStore from "stores/contextStore";

type SelectSkuProps = {
  organizationId?: string;
  value?: string | null;
  productTypeId?: string;
  hasSkuContents?: boolean;
  tagIds?: string[];
  onChange: (id: string | null) => void;
  onSkuChange?: (sku: Skus | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<Skus>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectSku = ({
  organizationId,
  value: skuId,
  productTypeId,
  hasSkuContents,
  tagIds,
  onChange,
  onSkuChange,
  ...rest
}: SelectSkuProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [sku, setSku] = useState<Skus | null>(null);
  const { organizationId: contextOrgId } = contextStore.useState();

  // Load the initial value if we're passed a skuId
  const { isLoading, data } = useQuery<ResponseOfSkus>(
    ["sku", skuId],
    () => skuService.getSkuById(skuId!),
    {
      enabled: !!skuId && (!sku || sku.id !== skuId),
    }
  );

  useEffect(() => {
    // Once the initial value loads, update the value (if is hasn't been set by a search)
    if (data?.data) {
      setSku(data?.data!);
    }
  }, [data, sku]);

  const fetchSkus = async (searchTerm: string) => {
    const request = GetSkusRequest.fromJS({
      organizationId: organizationId ?? contextOrgId,
      pageNumber: 0,
      pageSize: 20,
      parameters: SkuFilter.fromJS({
        sku: searchTerm === "" ? undefined : searchTerm,
        productTypeId,
        hasSkuContents,
        tagIds,
      }),
      sortField: "sku",
    });
    const result = await queryClient.fetchQuery<ListResponseOfSkuListItem>(
      ["skus", request],
      () => skuService.getSkus(request)
    );

    return { result, forSearchTerm: searchTerm };
  };

  const onChangeSku = (sku: Skus | null) => {
    setSku(sku);
    onChange(sku?.id ?? null);
    if (onSkuChange) {
      onSkuChange(sku);
    }
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={sku}
      isLoadingInitialValue={isLoading || rest.loading}
      labelField="sku"
      idField="id"
      fetchData={fetchSkus}
      failMessage={t({ id: "selectSkuFetchFailed" })}
      onChange={onChangeSku}
    />
  );
};

export default SelectSku;
